import type { ProceduralHintRow, SafeProceduralWorkTemplate } from './worksheetTypes';

function digitCount(value: number): number {
  const safe = Math.abs(Math.trunc(value));
  return safe === 0 ? 1 : Math.floor(Math.log10(safe)) + 1;
}

function multiplierDigitsFromRight(multiplier: number): number[] {
  return String(Math.abs(Math.trunc(multiplier)))
    .split('')
    .reverse()
    .map((digit) => Number(digit));
}

/**
 * Produces a STUDENT-SAFE procedural shape. The returned object deliberately
 * excludes numeric partial products, final answers, and carry values.
 *
 * This function is intended for trusted/content-authoring generation. The
 * browser renderer should receive only the resulting safe template.
 */
export function buildLongMultiplicationWorkTemplate(
  multiplicand: number,
  multiplier: number,
): SafeProceduralWorkTemplate {
  if (!Number.isInteger(multiplicand) || !Number.isInteger(multiplier)) {
    throw new Error('Long multiplication work templates require integer operands.');
  }
  if (multiplicand < 0 || multiplier < 0) {
    throw new Error('Recovery prototype supports non-negative integer operands only.');
  }

  const digits = multiplierDigitsFromRight(multiplier);
  const rows: ProceduralHintRow[] = digits.map((multiplierDigit, passIndex) => {
    const rawPartialProduct = multiplicand * multiplierDigit;
    return {
      rowId: `partial-${passIndex + 1}`,
      kind: 'partial_product' as const,
      digitCount: digitCount(rawPartialProduct),
      placeValueOffset: passIndex,
      showLeadingOperator: passIndex > 0,
    };
  });

  rows.push({
    rowId: 'final-result',
    kind: 'final_result',
    digitCount: digitCount(multiplicand * multiplier),
    placeValueOffset: 0,
    showLeadingOperator: false,
  });

  // Structural carry locations only. Values are never exposed here.
  const carryHints = digits.flatMap((_, passIndex) => {
    const operandDigitCount = digitCount(multiplicand);
    return Array.from({ length: Math.max(0, operandDigitCount - 1) }, (_, index) => ({
      columnFromRight: index + 1,
      passIndex,
    }));
  });

  return {
    algorithm: 'long_multiplication',
    version: 1,
    rows,
    carryHints,
  };
}
